import axios from "axios";
import UserService from "rc-app/services/UserService";
import {
  embedHost,
  embedUrl,
  snippetText,
  normalizeDomain,
} from "rc-app/utils/publishEmbedFlow";

/* ── Website Embed API client ──────────────────────────────────────────
   Backs the "Publish on your website" modal and the public embed page.
   Spec: docs/product specs/WEBSITE_EMBED_SPEC.md (v6)

   An embed key is a POINTER + PERMISSION SLIP, not a container: it resolves to
   an asset, carries the site it may render on, and attributes any lead captured
   through it. The asset itself is served from where it always lives — changing
   the render never requires the customer to re-paste anything.

   ── ONE KEY PER (ASSET, SITE) ──
   DT1 placed on two websites is TWO keys and two snippets (spec §7.1.1). Each
   key emits `frame-ancestors` for its OWN site only, so a snippet copied off a
   channel partner's page is inert on the developer's main site — which a single
   shared key could never guarantee, since it renders on every allowed domain by
   definition.

   ── SECURITY POSTURE ──
   The iframe URL is visible in the page source of any site that embeds it, so
   it can never be secret. What makes a stolen URL useless is `frame-ancestors`:
   the browser refuses to render our page on an unlisted host, and that is the
   one layer the embedding page cannot bypass. (The server-side Origin check is
   log-only — from inside our own iframe the Origin is always OUR host, never
   the customer's, so it could never have enforced anything. See spec §5.4.)
   An empty allowlist DISABLES the embed — fail closed, never open.

   ── LEAD CAPTURE IS PER SITE ──
   `lead_capture` lives on the allowlist entry, not the key (§11.2): the gate
   belongs to the WEBSITE, so every asset on that domain follows one toggle. The
   server has no per-placement override, so don't send one.

   ── THE ALLOWLIST PATCH IS ADDITIVE ──
   `updateEmbedSettings` UPSERTS — it adds and updates, and never removes.
   Removal is the explicit DELETE below. This is deliberate: a wholesale replace
   let two admins with the modal open silently wipe each other's domains.

   ── THE PUBLIC CALLS GO STRAIGHT TO THE BACKEND ──
   `getEmbedPayload` and `getEmbedFrameAncestors` send no token and need none:
   both routes are on the auth middleware's allow-list, and the embed hosts are
   on its CORS allow-list. They briefly went through our own Netlify function to
   work around each of those in turn; that proxy is gone.
*/

const RE = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate`;
const ACTIONS = `${RE}/actions`;
const VIEWS = `${RE}/views`;

const authHeaders = () => ({
  Authorization: `Bearer ${UserService.getToken()}`,
  "ngrok-skip-browser-warning": "disabled",
  "Content-Type": "application/json",
});

const publicHeaders = () => ({
  "ngrok-skip-browser-warning": "disabled",
  "Content-Type": "application/json",
});

/**
 * Server allowlist entry -> the shape the modal indexes sites by.
 * The server calls it `site_id`; placements bind to it, so it must round-trip.
 */
const siteView = (d) => ({
  id: String(d?.site_id || ""),
  host: String(d?.host || ""),
  include_subdomains: !!d?.include_subdomains,
  label: d?.label || "",
  lead_capture: d?.lead_capture !== false,
  // Passed through AS STORED, null included: null means nobody has chosen, which the modal
  // reads as "infer from whether anything is published here". Coercing it to a boolean would
  // turn "never asked" into a decision.
  embeds_enabled: d?.embeds_enabled ?? null,
});

/** What the allowlist PATCH accepts. Hosts are normalised again server-side. */
const domainWrite = (d) => ({
  host: normalizeDomain(d?.host),
  include_subdomains: !!d?.include_subdomains,
  label: d?.label || null,
  lead_capture: d?.lead_capture !== false,
  // Only sent once someone has chosen. The PATCH carries the whole list and the server leaves
  // an omitted field alone, so sending null here would be a no-op either way — but sending it
  // only when known keeps the wire honest about what was decided.
  ...(d?.embeds_enabled === true || d?.embeds_enabled === false
    ? { embeds_enabled: d.embeds_enabled } : {}),
});

/**
 * Mint a key for ONE ASSET ON ONE SITE. Agency is derived from the JWT.
 *
 * `project_id` is context; the subject is the (asset, site) pair. The asset is
 * typed rather than a bare meta-model id — digital twins are first-class, route
 * views are deliberately out of scope (no lead gate exists for them).
 *
 * The site must already be on the project's allowlist; the server rejects an
 * unknown `site_id` with a 400, so add the domain first.
 *
 * Idempotent server-side: an existing active placement for the same
 * (asset, site) comes back with `reused: true` rather than a second key.
 *
 * No `lead_capture` — that's per-site, see the header note.
 *
 * @param {{ project_id: string, experience_kind: "meta_model"|"digital_twin",
 *           experience_id: string, site_id: string, label?: string,
 *           published?: boolean }} payload
 */
export const createEmbedKey = async (payload) => {
  const r = await axios.post(`${ACTIONS}/embed/keys`, payload, {
    headers: authHeaders(),
  });
  return r.data;
};

/**
 * Every key for a project plus the project's site registry.
 * ABA/ASA only — agents share links, they don't publish to a company website.
 *
 * TWO server calls behind one client call: keys and the allowlist are separate
 * endpoints, but they are useless apart — a key's `site_id` only means
 * something against the registry, and a site with no keys can't be rendered.
 *
 * Soft-removed domains are filtered out server-side, so what comes back is what
 * the modal should show.
 *
 * @param {string} projectId
 */
export const listEmbedKeys = async (projectId, opts = {}) => {
  const [keysRes, settingsRes] = await Promise.all([
    axios.get(`${VIEWS}/embed/keys`, {
      headers: authHeaders(),
      params: {
        project_id: projectId,
        // Revoked placements are hidden by default: the modal manages what is live, and a
        // thrown-away key is not manageable. REPORTING is the exception — sessions and leads
        // still reference it, and without the record the only thing that can say why that
        // history is dead, the row shows with no explanation at all.
        ...(opts.includeRevoked ? { include_revoked: true } : {}),
      },
    }),
    axios.get(`${VIEWS}/embed/settings/${encodeURIComponent(projectId)}`, {
      headers: authHeaders(),
    }),
  ]);
  return {
    embed_keys: keysRes.data?.keys || [],
    embed_settings: {
      allowed_domains: (settingsRes.data?.allowed_domains || []).map(siteView),
    },
  };
};

/**
 * KEY-LEVEL update — `label`, `published`, and `revoke`.
 *
 * `published` is EMBED-SCOPED: it controls whether this placement renders
 * through its embed key. It does NOT touch the project page, share links,
 * guided tours or offline builds.
 *
 * It also NEVER destroys the key. Unpublishing and republishing must return the
 * identical code, because the customer's page already carries it — regenerating
 * would kill a live embed from one mis-click, with no way back.
 *
 * `open_mode` is "fullscreen" | "inline" — what the visitor's FIRST tap does.
 * Per placement, unlike the lead gate: the gate is per-site because a visitor
 * should be asked once per site, while this is a property of the page, and one
 * site routinely holds both a hero walkthrough and a small card.
 *
 * It must stay ABSENT on a key nobody has set it on. A server-side default of
 * "fullscreen" would override every `?fs=0` already pasted on a live page — see
 * `resolveOpenMode` for the precedence this relies on.
 *
 * Not here: domains and the lead gate (project/site-level, see
 * `updateEmbedSettings`).
 *
 * @param {string} key
 * @param {{ label?: string, published?: boolean, revoke?: boolean,
 *           open_mode?: "fullscreen"|"inline" }} payload
 */
export const updateEmbedKey = async (key, payload) => {
  const r = await axios.patch(
    `${ACTIONS}/embed/keys/${encodeURIComponent(key)}`,
    payload,
    { headers: authHeaders() },
  );
  return r.data;
};

/**
 * PROJECT-LEVEL settings — the site registry. ADDITIVE: adds new hosts and
 * updates existing ones, never removes (use `deleteEmbedSite`).
 *
 * Re-sending a host that was soft-removed REACTIVATES it with its original
 * `site_id`, so placements bound to it start rendering again rather than being
 * orphaned.
 *
 * The server normalises and validates each host — bare IPs, public suffixes and
 * shared-hosting domains are rejected with a 400 naming the offender.
 *
 * Host expansion is the server's: an entry for `site1.com` authorises
 * `site1.com` + `www.site1.com` always, and `*.site1.com` only when
 * `include_subdomains` is true.
 *
 * @param {string} projectId
 * @param {{ allowed_domains: Array<{ host: string, include_subdomains?: boolean,
 *           label?: string, lead_capture?: boolean }> }} payload
 */
export const updateEmbedSettings = async (projectId, payload) => {
  const r = await axios.patch(
    `${ACTIONS}/embed/settings/${encodeURIComponent(projectId)}`,
    { allowed_domains: (payload?.allowed_domains || []).map(domainWrite) },
    { headers: authHeaders() },
  );
  return {
    project_id: projectId,
    allowed_domains: (r.data?.allowed_domains || []).map(siteView),
  };
};

/**
 * Remove a website from the project. SOFT delete, keyed on `site_id`.
 *
 * Soft because sessions carry `embed_site_id`: hard-deleting would orphan every
 * historical row and the per-site analytics breakdown would lose its label — a
 * customer's own history going unlabelled because they tidied up a domain. The
 * entry drops out of render, frame-ancestors and the modal, but stays
 * resolvable for reporting.
 *
 * Still destructive for the customer: every key bound to this site stops
 * rendering, so the caller confirms with the live-embed count first. Re-adding
 * the same host through `updateEmbedSettings` brings it back with the same
 * `site_id`, so the recovery path is real.
 *
 * @param {string} projectId
 * @param {string} siteId
 */
export const deleteEmbedSite = async (projectId, siteId) => {
  const r = await axios.delete(
    `${ACTIONS}/embed/settings/${encodeURIComponent(projectId)}/domains/${encodeURIComponent(siteId)}`,
    { headers: authHeaders() },
  );
  return r.data;
};

/**
 * PUBLIC render payload for the embed page.
 *
 * Whitelisted deliberately — project + branding + asset reference only. No lead
 * data, no participants, no internals, and never the site registry: the array
 * would tell any page that frames an embed the customer's full domain list and
 * their per-site lead strategy. `lead_capture` comes back as one resolved
 * boolean for the key's own site.
 *
 * A 404 is the opaque answer for unknown / unpublished / revoked /
 * off-allowlist: render the neutral "no longer available" notice and do NOT
 * start a session. The server never returns an unpublished key, so anything
 * that comes back is live — hence `published: true`.
 *
 * `experience_url` + `meta_model_id` are populated for meta models and null for
 * digital twins, whose `experience_id` IS the Matterport viewer reference.
 *
 * @param {string} key
 */
export const getEmbedPayload = async (key) => {
  const r = await axios.get(
    `${VIEWS}/embed/render/${encodeURIComponent(key)}`,
    { headers: publicHeaders() },
  );
  return { ...(r.data || {}), published: true };
};

/**
 * The hosts this key may be framed on, for the Netlify Edge Function's
 * per-key `frame-ancestors` header.
 *
 * Fails CLOSED: an unknown / unpublished / revoked key returns an EMPTY list,
 * and an empty `frame-ancestors` tells the browser to refuse framing anywhere.
 * Never widen an empty result to a default — that would make a revoked embed
 * render again.
 *
 * @param {string} key
 * @returns {Promise<string[]>}
 */
export const getEmbedFrameAncestors = async (key) => {
  const r = await axios.get(
    `${VIEWS}/embed/frame-ancestors/${encodeURIComponent(key)}`,
    { headers: publicHeaders() },
  );
  return r.data?.frame_ancestors || [];
};

/* Re-exported so callers have one import for "everything embed". The logic
   itself lives in utils/publishEmbedFlow.ts, which is pure and unit-tested. */
export { embedHost, embedUrl, snippetText, normalizeDomain };

/**
 * @deprecated Back-compat for the portfolio Distribution page, which predates
 * per-site keys and still asks for a snippet from a key alone. New callers
 * should use `snippetText({ key, assetName, projectName })` from
 * utils/publishEmbedFlow, which also carries the full `allow` list.
 */
export const embedSnippet = (key, title = "Immersive walkthrough") =>
  snippetText({ key, assetName: title, projectName: "" });
